/**
 * Ablagefläche für Dateien.
 *
 * Eine Eigenheit, die oft vergessen wird und dann die ganze Funktion kostet:
 * Das GESAMTE Fenster ist Abwurfzone, nicht nur das Rechteck. Wer eine Datei
 * irgendwo auf die Seite zieht, muss die Fläche aufleuchten sehen — sonst
 * landet die Datei im Browser-Tab und öffnet einfach das PDF.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface AblagezoneProps {
  /** Erlaubte Endungen, z. B. ['.pdf', '.xml']. */
  endungen: string[];
  mehrfach?: boolean;
  gross?: boolean;
  titel: string;
  hinweis: string;
  onDateien: (dateien: File[]) => void;
  kind?: React.ReactNode;
}

export function Ablagezone({
  endungen,
  mehrfach = false,
  gross = false,
  titel,
  hinweis,
  onDateien,
  kind,
}: AblagezoneProps) {
  const [ueber, setUeber] = useState(false);
  const [falsch, setFalsch] = useState(false);
  const eingabe = useRef<HTMLInputElement>(null);
  /** Zähler statt Boolean: dragenter/dragleave feuern für jedes Kindelement. */
  const tiefe = useRef(0);

  const passt = useCallback(
    (datei: File) => endungen.some((e) => datei.name.toLowerCase().endsWith(e.toLowerCase())),
    [endungen],
  );

  // Fensterweite Erkennung: Sobald eine Datei irgendwo über der Seite
  // schwebt, leuchtet die Fläche.
  useEffect(() => {
    function rein(e: DragEvent) {
      if (!e.dataTransfer?.types.includes('Files')) return;
      tiefe.current++;
      setUeber(true);
    }
    function raus() {
      tiefe.current = Math.max(0, tiefe.current - 1);
      if (tiefe.current === 0) {
        setUeber(false);
        setFalsch(false);
      }
    }
    function drueber(e: DragEvent) {
      // Ohne preventDefault öffnet der Browser die Datei selbst.
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    }
    function fallen(e: DragEvent) {
      e.preventDefault();
      tiefe.current = 0;
      setUeber(false);
    }
    window.addEventListener('dragenter', rein);
    window.addEventListener('dragleave', raus);
    window.addEventListener('dragover', drueber);
    window.addEventListener('drop', fallen);
    return () => {
      window.removeEventListener('dragenter', rein);
      window.removeEventListener('dragleave', raus);
      window.removeEventListener('dragover', drueber);
      window.removeEventListener('drop', fallen);
    };
  }, []);

  function uebernehmen(liste: FileList | null) {
    const dateien = [...(liste ?? [])];
    if (!dateien.length) return;
    const gute = dateien.filter(passt);
    if (!gute.length) {
      setFalsch(true);
      setTimeout(() => setFalsch(false), 2500);
      return;
    }
    onDateien(mehrfach ? gute : [gute[0]]);
  }

  return (
    <div
      className={`dz${gross ? ' tall' : ' short'}${ueber ? ' ueber' : ''}${falsch ? ' falsch' : ''}`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        uebernehmen(e.dataTransfer.files);
      }}
      onClick={() => eingabe.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          eingabe.current?.click();
        }
      }}
    >
      <div className="ic">{kind}</div>
      <h3>
        {falsch
          ? `Nur ${endungen.join(', ')}`
          : ueber
            ? 'Loslassen zum Hochladen'
            : titel}
      </h3>
      <p>{hinweis}</p>
      <input
        ref={eingabe}
        type="file"
        hidden
        multiple={mehrfach}
        accept={endungen.join(',')}
        onChange={(e) => {
          uebernehmen(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

/** Die vier Schritte der Verarbeitung, mit laufender Anzeige. */
export function Schritte({ aktiv, schritte }: { aktiv: number; schritte: string[] }) {
  return (
    <div className="steps">
      {schritte.map((text, i) => (
        <div
          key={text}
          className={`step${i < aktiv ? ' fertig' : i === aktiv ? ' laeuft' : ''}`}
        >
          <span className="stepdot">{i < aktiv ? '✓' : ''}</span>
          <span style={{ flex: 1 }}>{text}</span>
        </div>
      ))}
    </div>
  );
}
